import React from "react";
import useFetch from "react-fetch-hook";

import {
  processResponse,
  getBooleanValue,
} from "../Navigation/navigation.functions";
import BCAHeader from "./BCAHeader";
import { MyBCALinks } from "./bcaHeader.type";

export interface BCAHeaderWithAPIProps {
  /**
    Urls to fetch data and execute login logout and register actions
  */
  urls: {
    myBCAInfo: string; // url to get my bca links
    navLinks: string; // url to get the structure of links for the main navigation
    searchRootURL: string;
    login: string;
    logout: string;
    register?: string;
    myBCALink?: string;
    vehicleOptions?: string;
    auctionViewRoot?: string;
  };
  /**
    Information about the user, optional param, when skipped it the header
    will be in the not authenticated state
  */
  user?: {
    firstName: string;
    lastName: string;
  };
  /**
   * A default value for the search input
   */
  defaultSearchTerm?: string;
}

export function processMyBCA(response: any): MyBCALinks {
  if (response && response["@nodes"]) {
    return {
      ...(response["Selling"] && {
        selling: {
          id: response["Selling"]["@id"],
          label: response["Selling"].label,
          link: response["Selling"].link,
          shouldOpenInNewWindow: getBooleanValue(
            response["Selling"].shouldOpenInNewWindow
          ),
          isVisible: getBooleanValue(response["Selling"].isVisible),
          links: response["Selling"]["@nodes"].map((nodeName: string) => {
            return {
              id: response["Selling"][nodeName]["@id"],
              label: response["Selling"][nodeName].label,
              link: response["Selling"][nodeName].link,
              shouldOpenInNewWindow: getBooleanValue(
                response["Selling"][nodeName].shouldOpenInNewWindow
              ),
              isVisible: getBooleanValue(
                response["Selling"][nodeName].isVisible
              ),
            };
          }),
        },
      }),
      ...(response["Buying"] && {
        buying: {
          id: response["Buying"]["@id"],
          label: response["Buying"].label,
          link: response["Buying"].link,
          shouldOpenInNewWindow: getBooleanValue(
            response["Buying"].shouldOpenInNewWindow
          ),
          isVisible: getBooleanValue(response["Buying"].isVisible),
          links: response["Buying"]["@nodes"].map((nodeName: string) => {
            return {
              id: response["Buying"][nodeName]["@id"],
              label: response["Buying"][nodeName].label,
              link: response["Buying"][nodeName].link,
              shouldOpenInNewWindow: getBooleanValue(
                response["Buying"][nodeName].shouldOpenInNewWindow
              ),
              isVisible: getBooleanValue(
                response["Buying"][nodeName].isVisible
              ),
            };
          }),
        },
      }),
      ...(response["Account-Management"] && {
        accountLink: {
          id: response["Account-Management"]["@id"],
          label: response["Account-Management"].label,
          link: response["Account-Management"].link,
          shouldOpenInNewWindow: getBooleanValue(
            response["Account-Management"].shouldOpenInNewWindow
          ),
          isVisible: getBooleanValue(response["Account-Management"].isVisible),
        },
      }),
    };
  }
  return {};
}

export function processVehicleOptions(
  response: any
): { shortName: string; withCount: string }[] {
  if (response) {
    return response.map((type: any) => ({
      shortName: type.Description === "*" ? "All" : type.Description,
      withCount: type.ResourcedDescription,
    }));
  }
  return [];
}

export function processUserSearches(response: any): {
  savedSearches: { label: string; url: string }[];
  recentSearches: { label: string; url: string }[];
} {
  if (response) {
    return {
      savedSearches: response
        .filter((search: any) => search.UserSearchType === 1)
        .map((search: any) => ({
          label: search.Description,
          url: search.Url,
        })),
      recentSearches: response
        .filter((search: any) => search.UserSearchType === 0)
        .map((search: any) => ({
          label: search.Description,
          url: search.Url,
        })),
    };
  }
  return { savedSearches: [], recentSearches: [] };
}

export default function BCAHeaderWithAPI({
  user,
  urls,
  defaultSearchTerm,
}: BCAHeaderWithAPIProps) {
  const { data: navLinks } = useFetch(urls.navLinks);
  const { data: myBCAItems } = useFetch(urls.myBCAInfo);
  const { data: vehicleOptions } = useFetch(urls.vehicleOptions || "");

  const userSearchesUrl = user
    ? `${urls.auctionViewRoot}buyer/api/v1_0/FacetedSearchApi/UserSearches?cultureCode=en`
    : "";
  const defaultNavItems = [
    {
      id: "9bf0839a-f454-429f-b9c3-ed234b218fea",
      shouldOpenInNewWindow: false,
      label: "Search",
      link: `${urls.auctionViewRoot}Buyer/FacetedSearch`,
      isVisible: true,
    },
    {
      id: "67f10467-9e85-4b4d-857d-114685a75ca5",
      shouldOpenInNewWindow: false,
      label: "Sales",
      link: "https://www.bca.co.uk/sales",
      isVisible: true,
    },
    {
      id: "bac400e6-a8b4-4b93-88c4-37dfa5d99a38",
      shouldOpenInNewWindow: false,
      label: "Locations",
      link: "https://www.bca.co.uk/locations/",
      isVisible: true,
    },
    {
      id: "ecaead12-6dc5-4451-a56b-db80f7d4e1af",
      shouldOpenInNewWindow: false,
      label: "Live Online",
      link: "https://www.bca-online-auctions.co.uk/login.html",
      isVisible: true,
    },
    {
      id: "a7611c2e-145c-45ac-80a5-a74606e9abd6",
      shouldOpenInNewWindow: false,
      label: "Buying",
      link: "https://www.bca.co.uk/buy/",
      isVisible: true,
    },
    {
      id: "e32fb889-1711-4eda-a27a-765bad35e761",
      shouldOpenInNewWindow: false,
      label: "Selling",
      link: "https://www.bca.co.uk/sell/",
      isVisible: true,
    },
    {
      id: "1fca63c5-90eb-4f1f-a883-45b3e31c8863",
      shouldOpenInNewWindow: false,
      label: "Products & Services",
      link: "https://www.bca.co.uk/Services/",
      isVisible: true,
    },
    {
      id: "4fcfd46f-cc36-4001-a578-12d454c491b9",
      shouldOpenInNewWindow: false,
      label: "Support",
      link: "https://www.bca.co.uk/Help/",
      isVisible: true,
    },
  ];

  const { data: userSearches } = useFetch(userSearchesUrl);
  const processedNavLinks = processResponse(navLinks);

  return (
    <BCAHeader
      navItems={
        processedNavLinks.length > 0 ? processedNavLinks : defaultNavItems
      }
      myBCALinks={processMyBCA(myBCAItems)}
      vehicleOptions={processVehicleOptions(vehicleOptions)}
      user={user}
      urls={{
        login: urls.login,
        logout: urls.logout,
        auctionViewRoot: urls.auctionViewRoot || "",
        register: urls.register,
        myBCALink: urls.myBCALink,
        searchRootURL: urls.searchRootURL,
      }}
      recentSearches={processUserSearches(userSearches).recentSearches}
      savedSearches={processUserSearches(userSearches).savedSearches}
      defaultSearchTerm={defaultSearchTerm}
    ></BCAHeader>
  );
}
